import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Coins, MapPin, MessageCircle, Send, Video } from "lucide-react";
import { api, imgSrc, ROLE_LABEL } from "@/lib/api";
import { Loader } from "@/components/Guards";
import { TierBadge, VerifiedBadge } from "@/components/MemberBits";
import { SendMoneyDialog, TipDialog } from "@/components/MoneyDialogs";

const ActionBtn = ({ icon: Icon, children, red, ...p }) => (
  <button {...p} className={`flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors ${red ? "btn-red" : "border border-white/20 hover:border-white hover:bg-white hover:text-black"}`}>
    <Icon className="h-4 w-4" />{children}
  </button>
);

export default function MemberProfile() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  useEffect(() => { api.get(`/members/${id}`).then((r) => setM(r.data)); }, [id]);
  if (!m) return <Loader />;
  return (
    <main className="mx-auto grid max-w-7xl gap-12 px-5 py-12 lg:grid-cols-[5fr_6fr] lg:px-8" data-testid="member-profile-page">
      <div className="card-lux overflow-hidden"><img src={imgSrc(m.photo)} alt={m.name} className="aspect-[4/5] w-full object-cover" /></div>
      <div className="rise flex flex-col justify-center">
        <div className="mb-4 flex gap-2"><TierBadge tier={m.membership} /><VerifiedBadge status={m.id_status} /></div>
        <p className="eyebrow mb-2 !text-red-400">{ROLE_LABEL[m.role_type]}</p>
        <h1 data-testid="member-name" className="font-display text-6xl">{m.name}{m.age ? `, ${m.age}` : ""}</h1>
        <p className="mt-3 flex items-center gap-1 text-zinc-400"><MapPin className="h-4 w-4" />{[m.city, m.country].filter(Boolean).join(", ") || "Worldwide"}</p>
        <p className="mt-8 max-w-lg leading-relaxed text-zinc-300">{m.bio || "This member hasn't written a bio yet."}</p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to={`/messages/${m.id}`}><ActionBtn icon={MessageCircle} red data-testid="profile-message-btn">Message</ActionBtn></Link>
          <TipDialog to={m} trigger={<ActionBtn icon={Coins} data-testid="profile-tip-btn">Send tokens</ActionBtn>} />
          <SendMoneyDialog to={m} trigger={<ActionBtn icon={Send} data-testid="profile-send-cash-btn">Send cash</ActionBtn>} />
          {m.live_room_id && <Link to={`/cams/${m.live_room_id}`}><ActionBtn icon={Video} data-testid="profile-watch-live-btn">Watch live</ActionBtn></Link>}
        </div>
        {m.content_count > 0 && (
          <Link to="/market" data-testid="profile-content-link" className="mt-10 text-sm text-zinc-400 hover:text-white">
            {m.content_count} exclusive {m.content_count === 1 ? "set" : "sets"} available →
          </Link>
        )}
      </div>
    </main>
  );
}
